import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { Modal, Form, Input, Button, Checkbox, Tag, Typography, Spin, Space } from 'antd';
import API from '../../../../../API';
import { useAlertTemplate } from '../../../../../hooks/useDataHooks';
import { notifySuccess, notifyError } from '../../../../../Util';

const { Text } = Typography;
const { TextArea } = Input;

const countEmails = (raw) => (raw || '').split(/[,;\r\n]+/).filter((email) => email.trim()).length;

const SendAlertModal = ({ visible, onClose, projectId, council, period, onSent }) => {
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [toEmails, setToEmails] = useState('');
    const [ccEmails, setCcEmails] = useState('');
    const [bccEmails, setBccEmails] = useState('');
    const [saveAsDefault, setSaveAsDefault] = useState(false);
    const [showVariables, setShowVariables] = useState(false);
    const [showCc, setShowCc] = useState(false);
    const [showBcc, setShowBcc] = useState(false);
    const [sending, setSending] = useState(false);
    const descriptionRef = useRef(null);

    const { template, loading } = useAlertTemplate(projectId, council?.council_id, period, visible);

    useEffect(() => {
        if (visible) {
            setSaveAsDefault(false);
            setShowVariables(false);
        }
    }, [visible]);

    useEffect(() => {
        setTitle(template?.title || '');
        setDescription(template?.description || '');
        setToEmails(template?.to_emails || '');
        setCcEmails(template?.cc_emails || '');
        setBccEmails(template?.bcc_emails || '');
        setShowCc(!!template?.cc_emails);
        setShowBcc(!!template?.bcc_emails);
    }, [template]);

    const insertVariable = (name) => {
        const token = `{{${name}}}`;
        const input = descriptionRef.current?.resizableTextArea?.textArea;
        const at = input ? input.selectionStart : description.length;

        setDescription(description.slice(0, at) + token + description.slice(at));
    };

    const handleSend = async () => {
        try {
            setSending(true);
            const response = await API.sendAlert({
                project_id: projectId,
                council_id: council.council_id,
                report_start_date: period.report_start_date,
                report_end_date: period.report_end_date,
                title,
                description,
                to_emails: toEmails,
                cc_emails: ccEmails,
                bcc_emails: bccEmails,
                save_as_default: saveAsDefault
            });

            notifySuccess(`Alert sent to ${response.data.recipient_count} recipients`);
            onSent();
            onClose();
        } catch (error) {
            notifyError(error?.response?.data?.message || 'Failed to send alert');
        } finally {
            setSending(false);
        }
    };

    return (
        <Modal
            title={`Send Alert${council?.council_name ? ` to ${council.council_name}` : ''}`}
            open={visible}
            onCancel={onClose}
            onOk={handleSend}
            okText="Send"
            okButtonProps={{ disabled: loading || !title || !description, loading: sending }}
            destroyOnClose
            width={640}
            bodyStyle={{ maxHeight: '60vh', overflow: 'auto', paddingRight: 16 }}
            style={{ top: 16 }}
        >
            {loading ? <Spin /> : (
                <Form layout="vertical">
                    <Form.Item label="Period">
                        <Space style={{ marginTop: 0 }}>
                            <Text>{period.report_start_date} to {period.report_end_date}</Text>
                            {template?.council && (
                                <>
                                    <Tag color={template.council.submission_status === 'Submitted' ? 'green' : 'red'}>
                                        {template.council.submission_status}
                                    </Tag>
                                    <Tag color={template.council.approval_status === 'Approved' ? 'green' : 'red'}>
                                        {template.council.approval_status}
                                    </Tag>
                                </>
                            )}
                        </Space>
                        {template?.last_sent_at && (
                            <div>
                                <Text type="secondary">
                                    Last sent {moment(template.last_sent_at).format('YYYY-MM-DD HH:mm')} to {template.to_emails}
                                </Text>
                            </div>
                        )}
                    </Form.Item>

                    <Form.Item label="Title">
                        <Input value={title} onChange={(e) => setTitle(e.target.value)} />
                    </Form.Item>

                    <Form.Item label="Description" style={{ marginBottom: 12 }}>
                        <TextArea
                            ref={descriptionRef}
                            rows={7}
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                        />
                        <div style={{ textAlign: 'right', marginTop: 4 }}>
                            <Button type="link" size="small" style={{ padding: 0 }} onClick={() => setShowVariables(!showVariables)}>
                                {showVariables ? 'Hide variables' : 'Variables'}
                            </Button>
                        </div>
                        {showVariables && (
                            <div style={{ textAlign: 'right' }}>
                                {(template?.variables || []).map((name) => (
                                    <Tag
                                        key={name}
                                        style={{ cursor: 'pointer', marginBottom: 4 }}
                                        onClick={() => insertVariable(name)}
                                    >
                                        {`{{${name}}}`}
                                    </Tag>
                                ))}
                            </div>
                        )}
                    </Form.Item>

                    <Form.Item label="To" style={{ marginBottom: 12 }}>
                        <TextArea
                            rows={2}
                            placeholder="email1@example.com, email2@example.com"
                            value={toEmails}
                            onChange={(e) => setToEmails(e.target.value)}
                        />
                        <div style={{ textAlign: 'right', marginTop: 4 }}>
                            <Button type="link" size="small" style={{ padding: 0 }} onClick={() => setShowCc(!showCc)}>
                                {!showCc && countEmails(ccEmails) ? `Cc (${countEmails(ccEmails)})` : 'Cc'}
                            </Button>
                            <Button type="link" size="small" style={{ padding: 0, marginLeft: 12 }} onClick={() => setShowBcc(!showBcc)}>
                                {!showBcc && countEmails(bccEmails) ? `Bcc (${countEmails(bccEmails)})` : 'Bcc'}
                            </Button>
                        </div>
                    </Form.Item>

                    {showCc && (
                        <Form.Item label="Cc">
                            <TextArea
                                rows={2}
                                placeholder="email1@example.com, email2@example.com"
                                value={ccEmails}
                                onChange={(e) => setCcEmails(e.target.value)}
                            />
                        </Form.Item>
                    )}

                    {showBcc && (
                        <Form.Item label="Bcc">
                            <TextArea
                                rows={2}
                                placeholder="email1@example.com, email2@example.com"
                                value={bccEmails}
                                onChange={(e) => setBccEmails(e.target.value)}
                            />
                        </Form.Item>
                    )}

                    <Form.Item>
                        <Checkbox checked={saveAsDefault} onChange={(e) => setSaveAsDefault(e.target.checked)}>
                            Save title, description, cc and bcc as the default for this project
                        </Checkbox>
                    </Form.Item>
                </Form>
            )}
        </Modal>
    );
};

export default SendAlertModal;
